import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  TrendingUp as TrendingUpIcon,
  ShoppingCart as ShoppingCartIcon,
  People as PeopleIcon,
  Inventory as InventoryIcon,
  Schedule as ClockIcon,
  Warning as WarningIcon,
  Settings as SettingsIcon,
  Gavel as DisputeIcon,
  Storefront as StorefrontIcon,
  Payments as PaymentsIcon,
  CheckCircle as CheckCircleIcon,
  History as HistoryIcon,
  Refresh as RefreshIcon,
  AccountBalanceWallet as WalletIcon,
} from '@mui/icons-material';
import dashboardService, { DashboardSnapshot, DashboardTimeSeries, AttentionLists as AttentionData } from '../services/dashboardService';
import financeService, { FinanceOverviewData } from '../services/financeService';
import DashboardCharts from '../components/dashboard/DashboardCharts';
import AttentionLists from '../components/dashboard/AttentionLists';
import AlertCenter from '../components/dashboard/AlertCenter';
import PlatformProfitSection from '../components/dashboard/profit/PlatformProfitSection';
import { AlertsResponse } from '../services/dashboardService';
import './Dashboard.css';

type RangeKey = 'today' | '7d' | '30d' | 'month';

const RANGES: { key: RangeKey; label: string }[] = [
  { key: 'today', label: 'امروز' },
  { key: '7d', label: '۷ روز' },
  { key: '30d', label: '۳۰ روز' },
  { key: 'month', label: 'این ماه' },
];

/** Build from/to ISO bounds for the selected range. */
function rangeToParams(key: RangeKey): { from?: string; to?: string } {
  const now = new Date();
  const to = now.toISOString();
  const start = new Date(now);
  switch (key) {
    case 'today':
      return {}; // backend defaults to start-of-today → now
    case '7d':
      start.setDate(start.getDate() - 6);
      start.setHours(0, 0, 0, 0);
      return { from: start.toISOString(), to };
    case '30d':
      start.setDate(start.getDate() - 29);
      start.setHours(0, 0, 0, 0);
      return { from: start.toISOString(), to };
    case 'month':
      start.setDate(1);
      start.setHours(0, 0, 0, 0);
      return { from: start.toISOString(), to };
    default:
      return {};
  }
}

const faNumber = (n: number) => (n ?? 0).toLocaleString('fa-IR');
const faToman = (n: number) => `${(n ?? 0).toLocaleString('fa-IR')} تومان`;

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [range, setRange] = useState<RangeKey>('today');
  const [data, setData] = useState<DashboardSnapshot | null>(null);
  const [series, setSeries] = useState<DashboardTimeSeries | null>(null);
  const [finance, setFinance] = useState<FinanceOverviewData | null>(null);
  const [attention, setAttention] = useState<AttentionData | null>(null);
  const [alerts, setAlerts] = useState<AlertsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const rangeLabel = RANGES.find((r) => r.key === range)?.label ?? 'امروز';

  const load = useCallback(async (key: RangeKey) => {
    setLoading(true);
    setError(null);
    const params = rangeToParams(key);
    // EVERY widget is best-effort and independent: a 403 (no permission) or error on one
    // section hides ONLY that section — the user stays on the dashboard and keeps seeing
    // every section they DO have access to. The api layer is told (silentForbidden) not to
    // redirect to /403 for these background calls.
    const [snap, ts, fin, att, alr] = await Promise.allSettled([
      dashboardService.getSnapshot(params),
      dashboardService.getTimeSeries(params),
      financeService.getOverview({ from: params.from, to: params.to, unit: 'IRT', scale: 'raw' }, { silentForbidden: true }),
      dashboardService.getAttention(),
      dashboardService.getAlerts(),
    ]);
    setData(snap.status === 'fulfilled' ? snap.value : null);
    setSeries(ts.status === 'fulfilled' ? ts.value : null);
    setFinance(fin.status === 'fulfilled' ? fin.value : null);
    setAttention(att.status === 'fulfilled' ? att.value : null);
    setAlerts(alr.status === 'fulfilled' ? alr.value : null);
    // Only surface a banner if the WHOLE dashboard failed (e.g. backend down) — not for
    // per-section permission denials, which are handled by simply hiding that section.
    if ([snap, ts, fin, att, alr].every((r) => r.status === 'rejected')) {
      setError('بارگذاری داشبورد ناموفق بود. اتصال شبکه یا دسترسی خود را بررسی کنید.');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load(range);
  }, [range, load]);

  const t = data?.today;
  const live = data?.live;

  const statCards = [
    { name: `فروش ${rangeLabel}`, value: t ? faToman(t.sales) : '—', icon: <TrendingUpIcon />, color: 'blue' },
    { name: `درآمد پلتفرم ${rangeLabel}`, value: t ? faToman(t.platformRevenue) : '—', icon: <PaymentsIcon />, color: 'green' },
    { name: `سفارش ${rangeLabel}`, value: t ? faNumber(t.ordersCount) : '—', icon: <ShoppingCartIcon />, color: 'purple' },
    { name: 'سفارش موفق', value: t ? faNumber(t.ordersSuccessful) : '—', icon: <CheckCircleIcon />, color: 'sky' },
    { name: `کاربر جدید ${rangeLabel}`, value: t ? faNumber(t.newUsers) : '—', icon: <PeopleIcon />, color: 'blue' },
    { name: `فروشنده جدید ${rangeLabel}`, value: t ? faNumber(t.newStores) : '—', icon: <StorefrontIcon />, color: 'green' },
    { name: 'در انتظار تحویل', value: live ? faNumber(live.ordersPending) : '—', icon: <ClockIcon />, color: 'amber' },
  ];

  // عملیات نیازمند اقدام (خلاصه‌ی شمارشی) — رنگ قرمز اگر بیشتر از صفر
  const attentionSummary = [
    {
      label: 'برداشت‌های در انتظار',
      value: live ? `${faNumber(live.pendingWithdrawals.count)} مورد` : '—',
      hint: live ? faToman(live.pendingWithdrawals.amount) : '',
      count: live?.pendingWithdrawals.count ?? 0,
      icon: <WalletIcon style={{ width: 20, height: 20, color: '#f59e0b' }} />,
      onClick: () => navigate('/user-withdrawal-requests'),
    },
    {
      label: 'اختلافات باز',
      value: live ? faNumber(live.openDisputes) : '—',
      count: live?.openDisputes ?? 0,
      icon: <DisputeIcon style={{ width: 20, height: 20, color: '#ef4444' }} />,
      onClick: () => navigate('/dispute-center'),
    },
    {
      label: `سفارش مشکل‌دار (بیش از ۲۴ ساعت در پردازش)`,
      value: live ? faNumber(live.problematicOrders) : '—',
      count: live?.problematicOrders ?? 0,
      icon: <WarningIcon style={{ width: 20, height: 20, color: '#f97316' }} />,
      onClick: () => navigate('/orders'),
    },
    {
      label: 'محصولات در انتظار تایید',
      value: live ? faNumber(live.products.pendingApproval) : '—',
      count: live?.products.pendingApproval ?? 0,
      icon: <InventoryIcon style={{ width: 20, height: 20, color: '#0ea5e9' }} />,
      onClick: () => navigate('/product-approvals'),
    },
  ];

  const productRows = [
    { label: 'محصولات فعال', value: live ? faNumber(live.products.active) : '—', danger: false },
    { label: 'محصولات ناموجود', value: live ? faNumber(live.products.outOfStock) : '—', danger: (live?.products.outOfStock ?? 0) > 0 },
    { label: 'محصولات کم‌موجودی', value: live ? faNumber(live.products.lowStock) : '—', danger: (live?.products.lowStock ?? 0) > 0 },
    { label: 'در انتظار تایید', value: live ? faNumber(live.products.pendingApproval) : '—', danger: (live?.products.pendingApproval ?? 0) > 0 },
  ];

  return (
    <div className="dashboard" dir="rtl">
      {/* Header + range filter */}
      <div className="dashboard-header" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 className="page-title">داشبورد</h1>
          <p className="page-subtitle">خوش آمدید، {user?.name}. خلاصه وضعیت سیستم.</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div className="dash-range">
            {RANGES.map((r) => (
              <button
                key={r.key}
                className={`dash-range-btn${range === r.key ? ' active' : ''}`}
                onClick={() => setRange(r.key)}
              >
                {r.label}
              </button>
            ))}
          </div>
          <button className="dash-refresh" onClick={() => load(range)} disabled={loading} title="بروزرسانی">
            <RefreshIcon style={{ width: 18, height: 18 }} />
          </button>
        </div>
      </div>

      {error && (
        <div className="admin-card" style={{ borderRight: '4px solid #ef4444' }}>
          <div className="admin-card-body" style={{ color: '#ef4444', display: 'flex', alignItems: 'center', gap: 8 }}>
            <WarningIcon style={{ width: 20, height: 20 }} />
            <span>{error}</span>
          </div>
        </div>
      )}

      {/* Risk / Alert Center — shown only when there are alerts to keep it prominent */}
      {alerts && alerts.alerts.length > 0 && <AlertCenter data={alerts} loading={loading} />}

      {/* Stats Grid — shown while loading (skeletons) or when the snapshot is accessible.
          Hidden entirely if the user has no access to the overview stats (403). */}
      {(loading || data) && (
        <div className="stats-grid">
          {statCards.map((stat) => (
            <div key={stat.name} className="stat-card">
              <div className="stat-card-inner">
                <div className="stat-card-content">
                  <div className={`stat-card-icon ${stat.color}`}>{stat.icon}</div>
                  <div className="stat-card-info">
                    <div className="stat-card-label">{stat.name}</div>
                    {loading ? (
                      <div className="stat-card-skeleton" />
                    ) : (
                      <div className="stat-card-value">{stat.value}</div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Finance KPI strip (from /admin/finance/overview) */}
      {finance && (
        <div className="admin-card">
          <div className="admin-card-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <h2 style={{ fontSize: '1rem' }}>خلاصه مالی ({rangeLabel})</h2>
            <button className="dash-range-btn active" onClick={() => navigate('/finance')}>
              جزئیات بیشتر ←
            </button>
          </div>
          <div className="admin-card-body">
            <div className="dash-kpi-strip">
              <div className="dash-kpi"><span className="dash-kpi-label">GMV (فروش ناخالص)</span><span className="dash-kpi-value">{faToman(finance.metrics.gmv)}</span></div>
              <div className="dash-kpi"><span className="dash-kpi-label">درآمد خالص</span><span className="dash-kpi-value">{faToman(finance.metrics.netRevenue)}</span></div>
              <div className="dash-kpi"><span className="dash-kpi-label">کمیسیون محقق‌شده</span><span className="dash-kpi-value">{finance.metrics.commission.realized != null ? faToman(finance.metrics.commission.realized) : '—'}</span></div>
              <div className="dash-kpi"><span className="dash-kpi-label">در انتظار آزادسازی (Escrow)</span><span className="dash-kpi-value">{faToman(finance.metrics.escrowPending)}</span></div>
              <div className="dash-kpi"><span className="dash-kpi-label">موجودی قابل برداشت</span><span className="dash-kpi-value">{faToman(finance.metrics.availableBalance)}</span></div>
              <div className="dash-kpi"><span className="dash-kpi-label">وجوه مسدود</span><span className="dash-kpi-value">{faToman(finance.metrics.blockedFunds)}</span></div>
            </div>
          </div>
        </div>
      )}

      {/* 💄 سود و زیان پلتفرم — بازهٔ زمانی مستقل؛ بدون مجوز admin_dashboard_profit_read پنهان می‌شود. */}
      <PlatformProfitSection />

      {/* Charts — hidden if the time-series endpoint is not accessible (403). */}
      {(loading || series) && <DashboardCharts data={series} loading={loading} />}

      {/* Needs attention + Products — both come from the snapshot's live data; hidden if
          the snapshot isn't accessible (403). */}
      {(loading || data) && (
      <div className="content-grid">
        {/* Operational / needs attention */}
        <div className="admin-card">
          <div className="admin-card-header">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h2>نیازمند اقدام</h2>
              <WarningIcon style={{ width: 20, height: 20, color: '#f59e0b' }} />
            </div>
          </div>
          <div className="admin-card-body">
            {attentionSummary.map((row) => (
              <button key={row.label} className="security-row dash-clickable" onClick={row.onClick} style={{ width: '100%', border: 'none', cursor: 'pointer' }}>
                <div className="security-row-label">
                  {row.icon}
                  <span>{row.label}</span>
                </div>
                <span className="security-row-value" style={{ color: row.count > 0 ? '#ef4444' : undefined, display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                  {row.value}
                  {row.hint ? <small style={{ fontSize: '0.7rem', fontWeight: 400, color: 'var(--color-text-secondary)' }}>{row.hint}</small> : null}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Products health */}
        <div className="admin-card">
          <div className="admin-card-header">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h2>وضعیت محصولات</h2>
              <InventoryIcon style={{ width: 20, height: 20, color: '#9ca3af' }} />
            </div>
          </div>
          <div className="admin-card-body">
            {productRows.map((row) => (
              <div key={row.label} className="security-row">
                <div className="security-row-label">
                  <span>{row.label}</span>
                </div>
                <span className="security-row-value" style={{ color: row.danger ? '#ef4444' : undefined }}>{row.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
      )}

      {/* Detailed needs-attention lists — hidden if the attention endpoint isn't accessible (403). */}
      {(loading || attention) && (
        <>
          <div className="dashboard-header" style={{ marginTop: 8 }}>
            <h2 className="page-title" style={{ fontSize: '1.15rem' }}>جزئیات نیازمند اقدام</h2>
          </div>
          <AttentionLists data={attention} loading={loading} />
        </>
      )}

      {/* Recent Activity (Phase 5) */}
      <div className="admin-card">
        <div className="admin-card-header">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <h2>فعالیت‌های اخیر</h2>
            <HistoryIcon style={{ width: 20, height: 20, color: '#9ca3af' }} />
          </div>
        </div>
        <div className="admin-card-body">
          <div className="empty-state">
            <ClockIcon />
            <p>لاگ فعالیت‌ها به‌زودی اینجا نمایش داده می‌شود</p>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="admin-card">
        <div className="admin-card-header">
          <h2>دسترسی سریع</h2>
        </div>
        <div className="admin-card-body">
          <div className="quick-actions-grid">
            <button className="quick-action-item" onClick={() => navigate('/finance')}>
              <PaymentsIcon />
              <span>داشبورد مالی</span>
            </button>
            <button className="quick-action-item" onClick={() => navigate('/orders')}>
              <ShoppingCartIcon />
              <span>سفارش‌ها</span>
            </button>
            <button className="quick-action-item" onClick={() => navigate('/dispute-center')}>
              <DisputeIcon />
              <span>مرکز داوری</span>
            </button>
            <button className="quick-action-item" onClick={() => navigate('/settings')}>
              <SettingsIcon />
              <span>تنظیمات</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
